import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { feedbackService } from '../services/feedback.service';
import RatingScaleSelector from '../components/RatingScaleSelector';
import type {
  DadosAgendamentoFeedbackContract,
  SubmeterAgendamentoFeedbackPayloadContract,
  SubmeterAgendamentoFeedbackResponseContract,
} from '@sstexames/shared-types';
import {
  Calendar,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Clock,
  ShieldCheck,
  User,
  HeartHandshake,
  ExternalLink,
  Briefcase,
  Car,
  HelpCircle,
} from 'lucide-react';

// Motivos pré-configurados para toque rápido no mobile
const MOTIVOS_AUSENCIA_RAPIDOS = [
  { id: 'trabalho', label: 'Imprevisto no trabalho', icon: Briefcase },
  { id: 'transporte', label: 'Trânsito ou transporte', icon: Car },
  { id: 'saude', label: 'Problema de saúde pessoal', icon: HeartHandshake },
  { id: 'esqueci', label: 'Esqueci o dia ou horário', icon: Clock },
  { id: 'outro', label: 'Outro motivo', icon: HelpCircle },
];

export default function AvaliacaoExperiencia() {
  const navigate = useNavigate();
  const { token: tokenParam } = useParams<{ token?: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || tokenParam || '';

  const isEmbedded = useMemo(() => {
    const ref = searchParams.get('ref');
    return ref === 'embeded' || ref === 'embedded';
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dados, setDados] = useState<DadosAgendamentoFeedbackContract | null>(null);

  // Estados das Respostas
  const [compareceu, setCompareceu] = useState<boolean | null>(null);
  const [notaAtendimento, setNotaAtendimento] = useState<number | null>(null);
  const [notaEndereco, setNotaEndereco] = useState<number | null>(null);
  const [notaLimpeza, setNotaLimpeza] = useState<number | null>(null);
  const [notaSimplicidade, setNotaSimplicidade] = useState<number | null>(null);
  const [notaVelocidade, setNotaVelocidade] = useState<number | null>(null);
  const [comentario, setComentario] = useState<string>('');
  const [motivoAusencia, setMotivoAusencia] = useState<string>('');
  const [motivoSelecionadoId, setMotivoSelecionadoId] = useState<string>('');

  // Índice da pergunta atual no fluxo "uma por vez"
  // -1: Pergunta inicial de presença ("Você compareceu?") quando desconhecido
  // 0 em diante: Perguntas sequenciais do fluxo
  // 999: Tela final de agradecimento
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<SubmeterAgendamentoFeedbackResponseContract | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Token de avaliação não fornecido na URL.');
      setLoading(false);
      return;
    }

    async function carregarDados() {
      try {
        setLoading(true);
        setError(null);
        const data = await feedbackService.getDados(token);
        setDados(data);

        if (data.ja_respondido) {
          setIsCompleted(true);
        } else if (!data.presenca_confirmada_previamente) {
          // Se a presença não era conhecida previamente, inicia na pergunta de presença (-1)
          setCompareceu(null);
          setCurrentQuestionIndex(-1);
        } else {
          // Já sabemos que compareceu previamente
          setCompareceu(true);
          setCurrentQuestionIndex(0);
        }
      } catch (err: unknown) {
        const errorMsg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Não foi possível carregar os dados desta pesquisa. O link pode ter expirado ou ser inválido.';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    }

    carregarDados();
  }, [token]);

  // Lista dinâmica de perguntas quando compareceu = true
  // 0: Atendimento Clínica
  // 1: Endereço Clínica
  // 2: Limpeza Clínica
  // 3: Simplicidade Tecnologia
  // 4: Velocidade Tecnologia
  // 5: Comentário / Envio
  const totalQuestionsPresente = 6;

  // Lista dinâmica de perguntas quando compareceu = false
  // 0: Motivo da ausência
  // 1: Simplicidade Tecnologia
  // 2: Velocidade Tecnologia
  // 3: Comentário / Envio
  const totalQuestionsAusente = 4;

  const totalQuestions = compareceu === false ? totalQuestionsAusente : totalQuestionsPresente;

  // Auto-avanço com micro-delay suave para dar feedback tátil da nota no mobile
  const handleScoreChangeWithAutoAdvance = (setter: (val: number) => void, val: number) => {
    setter(val);
    setTimeout(() => {
      setCurrentQuestionIndex((prev) => {
        if (prev < totalQuestions - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 280);
  };

  // Submissão do Feedback
  const handleFinalizar = async () => {
    if (!token || !dados) return;

    if (!notaSimplicidade || !notaVelocidade) {
      alert('Por favor, avalie a simplicidade e velocidade da tecnologia para concluir.');
      return;
    }

    try {
      setSubmitting(true);
      const finalMotivo =
        compareceu === false
          ? motivoAusencia.trim() ||
            MOTIVOS_AUSENCIA_RAPIDOS.find((m) => m.id === motivoSelecionadoId)?.label ||
            'Não informado'
          : null;

      const payload: SubmeterAgendamentoFeedbackPayloadContract = {
        compareceu: compareceu !== false,
        nota_clinica_atendimento: compareceu ? notaAtendimento : null,
        nota_clinica_endereco: compareceu ? notaEndereco : null,
        nota_clinica_limpeza: compareceu ? notaLimpeza : null,
        nota_tecnologia_simplicidade: notaSimplicidade,
        nota_tecnologia_velocidade: notaVelocidade,
        comentario: comentario.trim() || null,
        motivo_ausencia: finalMotivo,
      };

      const result = await feedbackService.submeterFeedback(token, payload);
      setSubmitResult(result);
      setIsCompleted(true);
    } catch (err: unknown) {
      const errorMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao enviar sua avaliação. Por favor, tente novamente.';
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Determina se o resultado final indica ausência ou presença
  const isAusente = useMemo(() => {
    if (submitResult) {
      return submitResult.compareceu === false || submitResult.status_agendamento_id === 21;
    }
    if (dados?.ja_respondido) {
      return dados.compareceu === false || (dados.compareceu == null && dados.status_agendamento.id === 21);
    }
    return compareceu === false;
  }, [submitResult, dados, compareceu]);

  const statusDescricao = useMemo(() => {
    if (submitResult?.status_agendamento_descricao) {
      return submitResult.status_agendamento_descricao;
    }
    if (dados?.ja_respondido) {
      return dados.status_agendamento.descricao;
    }
    if (compareceu === true) {
      return 'Presente';
    }
    if (compareceu === false) {
      return 'Não Compareceu';
    }
    return dados?.status_agendamento.descricao || 'Agendado';
  }, [submitResult, dados, compareceu]);

  const formatDateBR = (isoString?: string) => {
    if (!isoString) return '';
    const [year, month, day] = isoString.split('-');
    return `${day}/${month}/${year}`;
  };

  // Tela de Carregamento
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-sm border border-slate-200 text-center max-w-xs w-full">
          <div className="w-10 h-10 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <h2 className="text-sm font-bold text-slate-800">Carregando pesquisa...</h2>
          <p className="text-[11px] text-slate-500 mt-0.5">Preparando perguntas</p>
        </div>
      </div>
    );
  }

  // Tela de Erro
  if (error || !dados) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-sm border border-slate-200 text-center max-w-sm w-full">
          <div className="w-12 h-12 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-rose-100">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h2 className="text-base font-black text-slate-900">Link Indisponível</h2>
          <p className="text-xs text-slate-600 mt-2 leading-relaxed">{error}</p>
          {!isEmbedded && (
            <button
              type="button"
              onClick={() => navigate('/portal-colaborador')}
              className="mt-5 w-full py-3 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow transition-all cursor-pointer"
            >
              Ir para o Portal do Colaborador
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100 py-4 sm:py-8 px-3.5 sm:px-6 flex flex-col justify-between">
      <div className="max-w-md mx-auto w-full flex-1 flex flex-col justify-between">
        {/* Topo: Branding e Barra de Progresso Mobile */}
        <div>
          {/* Header Superior Branding Compacto */}
          <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-200/80">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg bg-emerald-600 text-white font-black text-xs flex items-center justify-center shadow-xs">
                SST
              </span>
              <span className="text-xs font-black text-slate-900 tracking-tight">
                SST Exames • Avaliação
              </span>
            </div>
            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 flex items-center gap-1">
              <Sparkles className="w-2.5 h-2.5" />
              1 Minuto
            </span>
          </div>

          {/* Card Resumo do Colaborador e Clínica (Header Fixo Foco no Mobile) */}
          <div className="bg-white/80 backdrop-blur-xs border border-slate-200 rounded-2xl p-3 mb-3 shadow-xs">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 truncate">
                <div className="w-6 h-6 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                  <User className="w-3.5 h-3.5" />
                </div>
                <div className="truncate">
                  <span className="block text-xs font-bold text-slate-900 truncate">
                    {dados.colaborador.nome}
                  </span>
                  <span className="text-[10px] text-slate-500 font-medium block truncate">
                    {dados.empresa.nome_fantasia || dados.empresa.razao_social}
                  </span>
                </div>
              </div>

              <div className="text-right flex-shrink-0">
                <span className="text-[10px] font-bold text-slate-600 block flex items-center gap-1 justify-end">
                  <Calendar className="w-3 h-3 text-slate-400" />
                  {formatDateBR(dados.data_agendamento)}
                </span>
                {dados.clinica && (
                  <span className="text-[10px] text-slate-400 block truncate max-w-[130px]">
                    {dados.clinica.nome}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Barra de Progresso Mobile "Pergunta X de Y" com Botão Voltar */}
          {!isCompleted && currentQuestionIndex >= 0 && (
            <div className="mb-3 bg-white border border-slate-200/80 rounded-2xl p-2.5 shadow-xs">
              <div className="flex items-center justify-between text-xs mb-1.5 px-0.5">
                <button
                  type="button"
                  disabled={currentQuestionIndex === 0 && !dados.presenca_confirmada_previamente}
                  onClick={() => {
                    if (currentQuestionIndex > 0) {
                      setCurrentQuestionIndex((prev) => prev - 1);
                    } else if (currentQuestionIndex === 0 && !dados.presenca_confirmada_previamente) {
                      setCurrentQuestionIndex(-1);
                    }
                  }}
                  className={`text-[11px] font-bold flex items-center gap-1 transition-colors ${
                    currentQuestionIndex > 0 || !dados.presenca_confirmada_previamente
                      ? 'text-slate-600 hover:text-slate-900 cursor-pointer'
                      : 'text-slate-300 cursor-not-allowed'
                  }`}
                >
                  <ArrowLeft className="w-3 h-3" />
                  <span>Voltar</span>
                </button>

                <span className="text-[11px] font-black text-slate-500 uppercase tracking-wider">
                  Pergunta {currentQuestionIndex + 1} de {totalQuestions}
                </span>

                <span className="text-[11px] font-bold text-emerald-600">
                  {Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100)}%
                </span>
              </div>

              <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-emerald-600 h-full rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: `${Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100)}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Card Interativo Principal com Transição Suave */}
        <div className="bg-white rounded-3xl p-5 sm:p-6 shadow-sm border border-slate-200 relative my-auto">
          {/* ======================================================== */}
          {/* CASO: TELA FINAL DE AGRADECIMENTO & RESUMO              */}
          {/* ======================================================== */}
          {isCompleted ? (
            <div className="space-y-4 text-center py-2 animate-question-slide">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto shadow-inner border border-emerald-200">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <div>
                <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  Muito obrigado pela avaliação!
                </h2>
                <p className="text-xs text-slate-600 mt-1 max-w-xs mx-auto leading-relaxed">
                  Seu feedback foi registrado e contribui diretamente para aprimorar os exames ocupacionais.
                </p>
              </div>

              {/* Card Resumo do Agendamento Atualizado */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-left text-xs space-y-2.5">
                <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                  <span className="font-bold text-slate-600 uppercase tracking-wider text-[10px]">
                    Status do Agendamento
                  </span>
                  <span
                    className={`px-2.5 py-0.5 rounded-full font-black text-[11px] inline-flex items-center gap-1 ${
                      isAusente
                        ? 'bg-amber-100 text-amber-800 border border-amber-200'
                        : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    }`}
                  >
                    {statusDescricao}
                  </span>
                </div>

                <div className="space-y-1 text-slate-600 text-[11px]">
                  <div>
                    <strong>Colaborador:</strong> {dados.colaborador.nome}
                  </div>
                  <div>
                    <strong>Empresa:</strong> {dados.empresa.nome_fantasia || dados.empresa.razao_social}
                  </div>
                  {dados.clinica && (
                    <div>
                      <strong>Clínica:</strong> {dados.clinica.nome}
                    </div>
                  )}
                  <div>
                    <strong>Data:</strong> {formatDateBR(dados.data_agendamento)}
                  </div>
                </div>

                {/* Orientação Contextual */}
                {isAusente ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 text-[11px] text-amber-900">
                    <p className="font-bold mb-0.5">Precisa reagendar seu exame?</p>
                    <p className="text-amber-800/90 leading-tight">
                      Como você informou ausência, procure o Recursos Humanos (RH) da sua empresa para emissão de nova guia.
                    </p>
                  </div>
                ) : (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 text-[11px] text-emerald-900">
                    <p className="font-bold mb-0.5">Aguardando emissão do ASO</p>
                    <p className="text-emerald-800/90 leading-tight">
                      Assim que o laudo for emitido pela clínica, ficará disponível no seu portal.
                    </p>
                  </div>
                )}
              </div>

              {!isEmbedded ? (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => navigate('/portal-colaborador')}
                    className="w-full py-3.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow transition-all inline-flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <span>Acessar Meu Portal do Colaborador</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="p-3.5 bg-slate-100 rounded-xl text-center text-xs text-slate-600 mt-2">
                  Avaliação concluída! Você já pode fechar esta tela no WhatsApp.
                </div>
              )}
            </div>
          ) : currentQuestionIndex === -1 ? (
            /* ======================================================== */
            /* ETAPA -1: PRESENÇA DESCONHECIDA                         */
            /* ======================================================== */
            <div key="step-presenca" className="space-y-4 animate-question-slide">
              <div className="text-center sm:text-left">
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-block mb-1.5">
                  Confirmação Inicial
                </span>
                <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                  Deu tudo certo com seu exame?
                </h2>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Seu agendamento estava programado para <strong>{formatDateBR(dados.data_agendamento)}</strong>. Você conseguiu comparecer na clínica?
                </p>
              </div>

              <div className="grid grid-cols-1 gap-2.5 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setCompareceu(true);
                    setCurrentQuestionIndex(0);
                  }}
                  className="p-4 rounded-2xl border-2 border-emerald-500 bg-emerald-50/60 hover:bg-emerald-50 text-emerald-900 font-bold text-left flex items-center gap-3 transition-all active:scale-98 cursor-pointer group shadow-xs"
                >
                  <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="block text-sm font-black text-emerald-950">Sim, compareci</span>
                    <span className="text-[11px] text-emerald-700 font-medium block">
                      Realizei o exame na clínica
                    </span>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setCompareceu(false);
                    setCurrentQuestionIndex(0);
                  }}
                  className="p-4 rounded-2xl border-2 border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-800 font-bold text-left flex items-center gap-3 transition-all active:scale-98 cursor-pointer group shadow-xs"
                >
                  <div className="w-9 h-9 rounded-xl bg-slate-400 text-white flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="block text-sm font-black text-slate-900">Não compareci</span>
                    <span className="text-[11px] text-slate-500 font-medium block">
                      Tive um imprevisto e não fui
                    </span>
                  </div>
                </button>
              </div>
            </div>
          ) : compareceu ? (
            /* ======================================================== */
            /* FLUXO PRESENÇA = SIM (1 PERGUNTA POR VEZ)               */
            /* ======================================================== */
            <div key={`question-pres-${currentQuestionIndex}`} className="animate-question-slide">
              {/* Pergunta 1: Atendimento Clínica */}
              {currentQuestionIndex === 0 && (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-block mb-1">
                      Clínica • Recepção
                    </span>
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Como foi o atendimento na clínica?
                    </h2>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Cortesia, agilidade e clareza da recepção em <strong>{dados.clinica?.nome || 'Clínica'}</strong>.
                    </p>
                  </div>

                  <div className="pt-1">
                    <RatingScaleSelector
                      value={notaAtendimento}
                      onChange={(val) =>
                        handleScoreChangeWithAutoAdvance(setNotaAtendimento, val)
                      }
                    />
                  </div>
                </div>
              )}

              {/* Pergunta 2: Endereço Clínica */}
              {currentQuestionIndex === 1 && (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-block mb-1">
                      Clínica • Localização
                    </span>
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      O que achou do endereço e acesso?
                    </h2>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Facilidade para localizar a clínica, transporte e pontos de referência.
                    </p>
                  </div>

                  <div className="pt-1">
                    <RatingScaleSelector
                      value={notaEndereco}
                      onChange={(val) =>
                        handleScoreChangeWithAutoAdvance(setNotaEndereco, val)
                      }
                    />
                  </div>
                </div>
              )}

              {/* Pergunta 3: Limpeza Clínica */}
              {currentQuestionIndex === 2 && (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-block mb-1">
                      Clínica • Limpeza
                    </span>
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Como estavam a limpeza e o ambiente?
                    </h2>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Higiene da sala de espera, consultórios médicos e banheiros.
                    </p>
                  </div>

                  <div className="pt-1">
                    <RatingScaleSelector
                      value={notaLimpeza}
                      onChange={(val) =>
                        handleScoreChangeWithAutoAdvance(setNotaLimpeza, val)
                      }
                    />
                  </div>
                </div>
              )}

              {/* Pergunta 4: Simplicidade Tecnologia */}
              {currentQuestionIndex === 3 && (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-block mb-1">
                      SST Exames • Simplicidade
                    </span>
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Como foi a facilidade do agendamento?
                    </h2>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Clareza das instruções, facilidade para abrir links e orientações.
                    </p>
                  </div>

                  <div className="pt-1">
                    <RatingScaleSelector
                      value={notaSimplicidade}
                      onChange={(val) =>
                        handleScoreChangeWithAutoAdvance(setNotaSimplicidade, val)
                      }
                    />
                  </div>
                </div>
              )}

              {/* Pergunta 5: Velocidade Tecnologia */}
              {currentQuestionIndex === 4 && (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-block mb-1">
                      SST Exames • Rapidez
                    </span>
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Qual a sua nota para a agilidade do serviço?
                    </h2>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Velocidade dos lembretes no WhatsApp e confirmação do agendamento.
                    </p>
                  </div>

                  <div className="pt-1">
                    <RatingScaleSelector
                      value={notaVelocidade}
                      onChange={(val) =>
                        handleScoreChangeWithAutoAdvance(setNotaVelocidade, val)
                      }
                    />
                  </div>
                </div>
              )}

              {/* Pergunta 6: Comentário e Conclusão */}
              {currentQuestionIndex === 5 && (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-block mb-1">
                      Última Etapa • Observações
                    </span>
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Quer deixar um elogio ou sugestão?
                    </h2>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Opcional. Seu comentário é confidencial e direcionado à equipe de qualidade.
                    </p>
                  </div>

                  <div>
                    <textarea
                      rows={3}
                      value={comentario}
                      onChange={(e) => setComentario(e.target.value)}
                      placeholder="Conte como foi sua experiência ou o que podemos melhorar..."
                      className="w-full p-3.5 rounded-2xl border border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-slate-50/50"
                    />
                  </div>
                </div>
              )}

              {/* Botões de Navegação no Rodapé da Pergunta */}
              <div className="pt-4 flex items-center justify-between gap-2 border-t border-slate-100 mt-4">
                {currentQuestionIndex > 0 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
                    className="py-2.5 px-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all cursor-pointer flex items-center gap-1"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Anterior</span>
                  </button>
                ) : (
                  <div />
                )}

                {currentQuestionIndex < 5 ? (
                  <button
                    type="button"
                    disabled={
                      (currentQuestionIndex === 0 && !notaAtendimento) ||
                      (currentQuestionIndex === 1 && !notaEndereco) ||
                      (currentQuestionIndex === 2 && !notaLimpeza) ||
                      (currentQuestionIndex === 3 && !notaSimplicidade) ||
                      (currentQuestionIndex === 4 && !notaVelocidade)
                    }
                    onClick={() => setCurrentQuestionIndex((prev) => prev + 1)}
                    className="py-2.5 px-5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black text-xs rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer ml-auto"
                  >
                    <span>Próxima</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={handleFinalizar}
                    className="py-3 px-6 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-60 text-white font-black text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-1.5 cursor-pointer ml-auto"
                  >
                    {submitting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <span>Concluir Avaliação</span>
                        <HeartHandshake className="w-3.5 h-3.5" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ) : (
            /* ======================================================== */
            /* FLUXO NÃO COMPARECEU (1 PERGUNTA POR VEZ)               */
            /* ======================================================== */
            <div key={`question-aus-${currentQuestionIndex}`} className="animate-question-slide">
              {/* Pergunta 1: Motivo da Ausência */}
              {currentQuestionIndex === 0 && (
                <div className="space-y-3.5">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-amber-800 bg-amber-50 px-2 py-0.5 rounded-md inline-block mb-1">
                      Ausência • Motivo
                    </span>
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Qual foi o motivo da ausência?
                    </h2>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Selecione a opção que melhor descreve o imprevisto:
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-2 pt-1">
                    {MOTIVOS_AUSENCIA_RAPIDOS.map((motivo) => {
                      const Icon = motivo.icon;
                      const isSelected = motivoSelecionadoId === motivo.id;

                      return (
                        <button
                          key={motivo.id}
                          type="button"
                          onClick={() => {
                            setMotivoSelecionadoId(motivo.id);
                            if (motivo.id !== 'outro') {
                              setMotivoAusencia(motivo.label);
                              setTimeout(() => {
                                setCurrentQuestionIndex(1);
                              }, 280);
                            }
                          }}
                          className={`p-3 rounded-xl border text-left flex items-center gap-2.5 transition-all cursor-pointer ${
                            isSelected
                              ? 'border-emerald-500 bg-emerald-50/70 text-emerald-950 font-bold shadow-xs'
                              : 'border-slate-200 bg-slate-50/70 text-slate-700 hover:bg-slate-100 hover:border-slate-300 font-medium'
                          }`}
                        >
                          <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-emerald-600' : 'text-slate-400'}`} />
                          <span className="text-xs flex-1">{motivo.label}</span>
                          {isSelected && <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                        </button>
                      );
                    })}

                    {motivoSelecionadoId === 'outro' && (
                      <input
                        type="text"
                        value={motivoAusencia}
                        onChange={(e) => setMotivoAusencia(e.target.value)}
                        placeholder="Descreva brevemente o motivo..."
                        className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white mt-1"
                        autoFocus
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Pergunta 2: Simplicidade Tecnologia */}
              {currentQuestionIndex === 1 && (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-block mb-1">
                      SST Exames • Simplicidade
                    </span>
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Como foi a facilidade do agendamento?
                    </h2>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Facilidade para entender as instruções e orientações enviadas.
                    </p>
                  </div>

                  <div className="pt-1">
                    <RatingScaleSelector
                      value={notaSimplicidade}
                      onChange={(val) =>
                        handleScoreChangeWithAutoAdvance(setNotaSimplicidade, val)
                      }
                    />
                  </div>
                </div>
              )}

              {/* Pergunta 3: Velocidade Tecnologia */}
              {currentQuestionIndex === 2 && (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-block mb-1">
                      SST Exames • Rapidez
                    </span>
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Qual sua nota para a agilidade dos avisos?
                    </h2>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Pontualidade e rapidez na comunicação e lembretes.
                    </p>
                  </div>

                  <div className="pt-1">
                    <RatingScaleSelector
                      value={notaVelocidade}
                      onChange={(val) =>
                        handleScoreChangeWithAutoAdvance(setNotaVelocidade, val)
                      }
                    />
                  </div>
                </div>
              )}

              {/* Pergunta 4: Comentários e Conclusão */}
              {currentQuestionIndex === 3 && (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md inline-block mb-1">
                      Última Etapa • Observações
                    </span>
                    <h2 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
                      Quer deixar alguma observação?
                    </h2>
                    <p className="text-xs text-slate-600 mt-0.5">
                      Opcional. Seus apontamentos nos ajudam a aperfeiçoar nossos serviços.
                    </p>
                  </div>

                  <div>
                    <textarea
                      rows={3}
                      value={comentario}
                      onChange={(e) => setComentario(e.target.value)}
                      placeholder="Deixe aqui qualquer detalhe ou sugestão..."
                      className="w-full p-3.5 rounded-2xl border border-slate-200 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-slate-50/50"
                    />
                  </div>
                </div>
              )}

              {/* Botões de Navegação no Rodapé da Pergunta */}
              <div className="pt-4 flex items-center justify-between gap-2 border-t border-slate-100 mt-4">
                {currentQuestionIndex > 0 ? (
                  <button
                    type="button"
                    onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
                    className="py-2.5 px-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all cursor-pointer flex items-center gap-1"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Anterior</span>
                  </button>
                ) : (
                  <div />
                )}

                {currentQuestionIndex < 3 ? (
                  <button
                    type="button"
                    disabled={
                      (currentQuestionIndex === 0 && !motivoSelecionadoId) ||
                      (currentQuestionIndex === 1 && !notaSimplicidade) ||
                      (currentQuestionIndex === 2 && !notaVelocidade)
                    }
                    onClick={() => setCurrentQuestionIndex((prev) => prev + 1)}
                    className="py-2.5 px-5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black text-xs rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer ml-auto"
                  >
                    <span>Próxima</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={handleFinalizar}
                    className="py-3 px-6 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-60 text-white font-black text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-1.5 cursor-pointer ml-auto"
                  >
                    {submitting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <span>Concluir Avaliação</span>
                        <HeartHandshake className="w-3.5 h-3.5" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Rodapé Seguro Mobile */}
        <div className="text-center mt-3 text-slate-400 text-[11px] flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
          <span>SST Exames • Avaliação Segura</span>
        </div>
      </div>
    </div>
  );
}
